/** @domain financial -- Cross-cutting numeric fields: shares, price, amount, percentage, rate, limits. */
import { z } from 'zod';
import { PRECISION_CURRENCY, PRECISION_STANDARD } from '../precision';
import { createDomainField } from './types';
import { commaFormatter, limitDecimalPlaces } from './formatters';

const define = createDomainField('financial');

// ─── Base types (unique schema/format/precision) ─────────────────────────────

export const shares = define({
  label: 'Shares',
  placeholder: 'Enter number of shares',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: commaFormatter,
  displayFormat: 'number',
  displayPrecision: 0,
});

export const price = define({
  label: 'Price',
  placeholder: 'Enter price',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,4})?$/, 'Must be a valid price'),
  adapterHint: 'text',
  formatter: limitDecimalPlaces(4),
  displayFormat: 'currency',
  displayPrecision: PRECISION_STANDARD,
});

export const amount = define({
  label: 'Amount',
  placeholder: 'Enter amount',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid amount'),
  adapterHint: 'text',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'currency',
  displayPrecision: PRECISION_CURRENCY,
});

export const percentage = define({
  label: 'Percentage',
  placeholder: 'Enter percentage',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const rate = define({
  label: 'Rate',
  placeholder: 'Enter rate',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,4})?$/, 'Must be a valid rate'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(4),
  displayFormat: 'percentage',
});

export const actionAmount = define({
  label: 'Amount',
  placeholder: 'Enter amount',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid amount'),
  adapterHint: 'text',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'action-link',
});

// ─── Named financial variants ────────────────────────────────────────────────

export const exercisePrice = define({
  ...price,
  label: 'Exercise Price',
  placeholder: 'Enter exercise price',
  schema: z
    .string()
    .regex(
      /^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,6})?$/,
      'Must be a valid price (up to 6 decimal places)',
    ),
  formatter: limitDecimalPlaces(6),
});

export const grantPrice = define({ ...price, label: 'Grant Price', placeholder: 'Enter grant price' });
export const marketValueAtGrant = define({
  ...price,
  label: 'FMV at Grant',
  placeholder: 'Enter FMV at grant',
});

export const intendedGrantValue = define({
  ...amount,
  label: 'Intended Grant Value',
  placeholder: 'Enter intended grant value',
  displayPrecision: PRECISION_STANDARD,
});

export const grantValue = define({
  ...price,
  label: 'Grant Value',
  displayFormat: 'currency',
  displayPrecision: PRECISION_STANDARD,
});

export const maximumAmount = define({
  ...amount,
  label: 'Maximum Amount',
  placeholder: 'Enter maximum amount',
});

export const baseRate = define({ ...rate, label: 'Base Rate', placeholder: 'Enter base rate' });

export const ytdWages = define({ ...amount, label: 'YTD Ordinary Income' });
export const ytdSupplementalIncome = define({ ...amount, label: 'YTD Supplemental Income' });

export const lowerLimit = define({ ...shares, label: 'Lower Limit', placeholder: 'Enter lower limit' });
export const upperLimit = define({ ...shares, label: 'Upper Limit', placeholder: 'Enter upper limit' });
